import { EntityId } from "@reduxjs/toolkit";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetLayerFeatureTotalsQuery, useGetLayersQuery } from "fond/api";
import { CollaborationCostToServeLayerIds, LayerIds, Maybe, MuPowerOutputLayerIds, NBNCTSOutputLayerIds, Project } from "fond/types";
import { metersToFeetDisplay } from "fond/utils";
import { isCollaborationProject, isMuPowerProject, isNBNCostToServeProject, isPlannerProject } from "fond/utils/project";

import { useGetScenarioConfiguration } from "./useGetScenarioConfiguration";

interface UseLayerFeatureTotalArgs {
  versionId: Maybe<string>;
  layerKey: string | undefined;
}

interface UseCostToServeMetricArgs {
  project: Maybe<Project>;
  versionId: Maybe<string>;
}

/**
 * Looks up the feature total (count and length) for a layer.
 */
const useLayerFeatureTotal = ({ versionId, layerKey }: UseLayerFeatureTotalArgs) => {
  const { data: layers } = useGetLayersQuery(versionId ?? skipToken, { skip: !versionId || !layerKey });
  const layerExists = !!layerKey && !!layers?.ids.some((id: EntityId) => layers.entities[id]?.LayerKey === layerKey);

  const { data: featureTotals } = useGetLayerFeatureTotalsQuery(versionId ? { versionId } : skipToken, { skip: !versionId || !layerExists });

  return layerExists ? featureTotals?.[layerKey as string] : undefined;
};

/**
 * Determines the key of the layer that represents service locations based on the project type.
 */
export const useServiceLocationLayerKey = (project: Maybe<Project>): string | undefined => {
  const isCollaboration = isCollaborationProject(project);
  const { configuration } = useGetScenarioConfiguration({ projectId: isCollaboration ? project.ID : undefined });

  if (isPlannerProject(project)) return LayerIds.serviceLocation;
  if (isNBNCostToServeProject(project)) return NBNCTSOutputLayerIds.serviceLocation;
  if (isCollaboration) return configuration?.ToolConfigurations.CostToServe.CostingLayer.LayerKey;
  if (isMuPowerProject(project)) return MuPowerOutputLayerIds.meters;

  return undefined;
};

/**
 * Counts the features in the version's service location layer.
 */
export const useServiceLocationsCount = ({ project, versionId }: UseCostToServeMetricArgs): number | undefined => {
  const layerKey = useServiceLocationLayerKey(project);
  return useLayerFeatureTotal({ versionId, layerKey })?.count ?? undefined;
};

/**
 * Determines the key of the layer that represents the fiber path based on the project type.
 */
const useFiberPathLayerKey = (project: Maybe<Project>): string | undefined => {
  const isCollaboration = isCollaborationProject(project);
  const { configuration, hasConfig: isCollaborationCostToServe } = useGetScenarioConfiguration({
    projectId: isCollaboration ? project.ID : undefined,
  });

  if (isPlannerProject(project)) return LayerIds.path;
  if (isNBNCostToServeProject(project)) return NBNCTSOutputLayerIds.path;
  if (isCollaborationCostToServe) {
    const configuredPathLayerKey = configuration?.PathLayer?.LayerKey;
    return configuredPathLayerKey || CollaborationCostToServeLayerIds.path;
  }

  // TODO: MuPower projects don't have a fiber path layer, we may add one in the future.

  return undefined;
};

/**
 * Gets the total length of the version's fiber path layer.
 */
export const useFiberPathLength = ({ project, versionId }: UseCostToServeMetricArgs): number | undefined => {
  const layerKey = useFiberPathLayerKey(project);
  const lengthMeters = useLayerFeatureTotal({ versionId, layerKey })?.length ?? undefined;

  if (lengthMeters === undefined) return undefined;
  return project?.SystemOfMeasurement === "imperial" ? metersToFeetDisplay(lengthMeters) : lengthMeters;
};
